import { FC } from "react";
import { useNavigate, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Box, Flex, Heading, Link, Text } from "@radix-ui/themes";

import { atlasRoutes } from "@/app/router/routes";
import { Button } from "@/components/button";
import { Uuid } from "@/domains/global/identifiers";

import { DataExportActions } from "../shared/actions";
import { DataExportSummary } from "../shared/summary";
import { DataExport, fetchDataExport } from "../shared/api";

export const DataExportsViewPage: FC = () => {
  const navigate = useNavigate();
  const { dataExportId } = useParams<{ dataExportId: Uuid }>();

  if (!dataExportId) {
    return <div>Invalid export ID.</div>;
  }

  const query = useQuery({
    queryKey: ["data-export", dataExportId],
    queryFn: async () => {
      const exportData = await fetchDataExport(dataExportId);
      if (!exportData) {
        throw new Error("Export not found");
      }
      return exportData;
    },
    staleTime: 30_000,
    retry: false,
  });

  if (query.isPending) {
    return <div>Loading export…</div>;
  }

  if (query.isError || !query.data) {
    const message = query.error instanceof Error ? query.error.message : "Failed to load export.";
    return <div>{message}</div>;
  }

  const dataExport = query.data;
  const temporalExportWorkflowUrl = buildWorkflowSearchUrl(
    "ExportDataForSpaceAccountWorkflow",
    dataExport.owned_by_space_account_id,
    dataExport.id
  );
  const temporalZipWorkflowUrl = buildExactWorkflowUrl(
    `CreateExportZipAndReturnDownloadLinkV2Workflow-${dataExport.id}`
  );

  return (
    <Box p="4" style={{ maxWidth: 880 }}>
      <Flex gap="3" align="center" mb="4" wrap="wrap">
        <Button size="2" variant="soft" onClick={() => navigate(atlasRoutes.dataExports({}).path)}>
          Back to exports
        </Button>
        <Button size="2" variant="outline" onClick={() => query.refetch()}>
          Refresh
        </Button>
      </Flex>

      <Heading size="6" mb="3">
        Data Export
      </Heading>

      <Text size="3" color="gray" mb="4">
        Manage export workflows and view storage information.
      </Text>

      <Box mb="4">
        <DataExportActions
          dataExport={dataExport}
          canDownload={Boolean(dataExport.export_zip_filepath)}
        />
      </Box>

      <Box mb="5">
        <Flex gap="3" mb="3" wrap="wrap">
          <Link href={temporalExportWorkflowUrl} target="_blank" rel="noreferrer">
            View Export Workflow in Temporal
          </Link>
          <Link href={temporalZipWorkflowUrl} target="_blank" rel="noreferrer">
            View Zip Workflow in Temporal
          </Link>
        </Flex>

        <DataExportSummary dataExport={dataExport} />
      </Box>

      <details>
        <summary style={{ cursor: "pointer", marginBottom: "8px" }}>Raw Response</summary>
        <pre
          style={{
            fontSize: "12px",
            background: "var(--gray-a3)",
            padding: "12px",
            borderRadius: "4px",
            overflow: "auto",
          }}
        >
          {JSON.stringify(dataExport, null, 2)}
        </pre>
      </details>
    </Box>
  );
};

const buildWorkflowSearchUrl = (
  workflowName: string,
  spaceAccountId: DataExport["owned_by_space_account_id"],
  dataExportId: DataExport["id"]
) => {
  const workflowIdPrefix = `${workflowName}-${spaceAccountId}-${dataExportId}`;
  const query = encodeURIComponent(`\`WorkflowId\` STARTS_WITH "${workflowIdPrefix}"`);
  return `https://cloud.temporal.io/namespaces/prod-backend-async-worker.rqp4d/workflows?query=${query}`;
};

const buildExactWorkflowUrl = (workflowId: string) => {
  const query = encodeURIComponent(`\`WorkflowId\` = "${workflowId}"`);
  return `https://cloud.temporal.io/namespaces/prod-backend-async-worker.rqp4d/workflows?query=${query}`;
};
